/**
 * Pure resolution model for the active legend, extracted from `ActiveLegend.svelte`
 * so the descriptor-to-rows logic is unit-testable without a map or the DOM.
 */

import type { Map as MapLibreMap, MapGeoJSONFeature } from 'maplibre-gl';
import type { ILayerSource } from '#lib/types.js';
import type { MapLayer } from '#lib/stores/legendStore.svelte.js';
import type { LegendLayer, LegendSection } from './types';
import { deriveActiveSymbology, type SymbologyItem } from './symbology';

export interface ResolvedRow {
	layerId: string;
	partOf?: string;
	source: ILayerSource | null;
	legend: MapLayer | null;
	layer: LegendLayer;
	subs: ResolvedRow[];
}

export interface CandidateSection {
	label: string;
	listRows: ResolvedRow[];
}

interface RenderedRow {
	layerId: string;
	label: string;
	partOf?: string;
	source: ILayerSource | null;
	legend: MapLayer | null;
	symbology: SymbologyItem[];
}

export interface RenderedSection {
	label: string;
	hints: SymbologyItem[];
	listRows: RenderedRow[];
}

/**
 * Resolve each descriptor layer against `systemData` + `legendStore`, keeping a
 * primary row only when it has a built source AND its `legendStore` entry is
 * switched on (`layerAnyEnabled`). A `partOf` sublayer never lists on its own;
 * it folds into its primary row's `subs`. Sections with no surviving row drop.
 */
export function resolveCandidateSections(
	sections: LegendSection[],
	systemData: Record<string, Record<string, ILayerSource | null>> | null,
	legendLayers: MapLayer[]
): CandidateSection[] {
	return sections
		.map((section) => {
			const candidates: ResolvedRow[] = section.layers.map((layer) => ({
				layerId: layer.layerId,
				partOf: layer.partOf,
				source: systemData?.[layer.moduleId]?.[layer.layerId] ?? null,
				legend: legendLayers.find((l) => l.systemId === layer.layerId) ?? null,
				layer,
				subs: []
			}));
			const listRows = candidates
				.filter((r) => !r.partOf && r.source && r.legend?.layerAnyEnabled)
				.map((r) => ({
					...r,
					subs: candidates.filter((o) => o.partOf === r.layerId && o.source)
				}));
			return { label: section.label, listRows };
		})
		.filter((section) => section.listRows.length > 0);
}

/**
 * Derive each row's symbology via the injected reader, lift surface hints to a
 * per-section caption (deduped by label), and drop a row with no symbology and
 * no fallback legend colour (an icon layer with none of its features in view).
 */
export function buildRenderedSections(
	candidateSections: CandidateSection[],
	symbologyOf: (layer: LegendLayer, source: ILayerSource | null) => SymbologyItem[]
): RenderedSection[] {
	// A single misbehaving layer must degrade to no symbology, never blank the legend.
	const safe = (layer: LegendLayer, source: ILayerSource | null): SymbologyItem[] => {
		try {
			return symbologyOf(layer, source);
		} catch {
			return [];
		}
	};
	return candidateSections
		.map((section) => {
			const rows = section.listRows.map((r) => ({
				r,
				all: [...safe(r.layer, r.source), ...r.subs.flatMap((s) => safe(s.layer, s.source))]
			}));
			const hints: SymbologyItem[] = [];
			for (const { all } of rows) {
				for (const it of all) {
					if (it.hint && !hints.some((h) => h.label === it.label)) hints.push(it);
				}
			}
			const listRows = rows
				.map(({ r, all }): RenderedRow => ({
					layerId: r.layerId,
					label: r.layer.label,
					partOf: r.partOf,
					source: r.source,
					legend: r.legend,
					symbology: all.filter((it) => !it.hint)
				}))
				.filter((row) => row.symbology.length > 0 || !!row.legend?.legendItems[0]?.color);
			return { label: section.label, hints, listRows };
		})
		.filter((section) => section.listRows.length > 0);
}

/**
 * Resolve + derive the active-legend sections against the live stores and map:
 * queries the switched-on layers' viewport features, then derives each row's
 * symbology. Shared by the on-screen ActiveLegend and the map-export renderer.
 */
export function computeRenderedSections(
	sections: LegendSection[],
	systemData: Record<string, Record<string, ILayerSource | null>> | null,
	legendLayers: MapLayer[],
	map: MapLibreMap | null
): RenderedSection[] {
	const candidateSections = resolveCandidateSections(sections, systemData, legendLayers);

	const wanted = candidateSections.flatMap((s) => s.listRows.flatMap((r) => [r, ...r.subs]));
	const layerIds = [
		...new Set(
			wanted
				.map((r) => r.source?.layer?.id)
				.filter((id): id is string => !!id && !!map?.getLayer(id))
		)
	];
	const byLayer = new Map<string, MapGeoJSONFeature[]>();
	if (map && layerIds.length) {
		for (const id of layerIds) byLayer.set(id, []);
		for (const feature of map.queryRenderedFeatures({ layers: layerIds, validate: false })) {
			byLayer.get(feature.layer.id)?.push(feature);
		}
	}

	const symbologyOf = (l: LegendLayer, source: ILayerSource | null): SymbologyItem[] => {
		if (l.hideSymbology) return [];
		const features = byLayer.get(source?.layer?.id ?? '') ?? [];
		if (l.deriveItems) return l.deriveItems(features);
		const style = source?.layer as { type?: string; paint?: unknown; layout?: unknown } | undefined;
		if (!style?.type) return [];
		return deriveActiveSymbology(style as never, l.symbology ?? {}, features);
	};

	return buildRenderedSections(candidateSections, symbologyOf);
}
